'use client';

import {
  getEmbeddedConnectedWallet,
  useIdentityToken,
  usePrivy,
  useWallets,
} from '@privy-io/react-auth';
import { useQuery } from '@tanstack/react-query';
import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { z } from 'zod';

import { zAccountsPayload, type zAccount, type zWallet } from '@/lib/api/accounts/schemas';
import { logger } from '@/lib/logger';

import { useFeature } from '@/hooks/useFeature';

import type { CachedWalletAddresses } from '@/utils/cachedWalletAddresses';
import { MATCHA_ACCOUNT } from '@/utils/featureFlags';

type Account = z.infer<typeof zAccount>;
type Wallet = z.infer<typeof zWallet>;

/**
 * Matcha Account session status for the Privy-only, internal-only build.
 *  - disabled: feature flag off (inert — no Privy/bootstrap work).
 *  - loading:  Privy still hydrating, or bootstrap in flight.
 *  - ready:    account session established.
 *  - error:    no trusted session — not authenticated, or bootstrap failed /
 *              timed out. Non-fatal: wallet-based features stay usable.
 */
export type AuthStatus = 'disabled' | 'loading' | 'ready' | 'error';

export type AuthContextValue = {
  status: AuthStatus;
  /** Initial UI hint, available independently of the Matcha Account session. */
  cachedWalletAddresses: CachedWalletAddresses | null;
  /** Present only when `status === 'ready'`. */
  account?: Account;
  /** Present only when `status === 'ready'`. */
  wallets?: Wallet[];
  /** Failure reason when `status === 'error'`. */
  error?: string;
};

/** Bound the bootstrap round-trip so a stalled request can't hang `loading`. */
const BOOTSTRAP_TIMEOUT_MS = 10_000;

/**
 * Cache key for the account-bootstrap query. Scoped to the Privy user *and* the
 * embedded wallet so a different identity (or a newly resolved wallet) refetches
 * rather than reusing another session's cached account under `staleTime: Infinity`.
 */
export const matchaAccountQueryKey = (userId: string | null, walletAddress: string | null) =>
  ['matcha-account', 'bootstrap', userId, walletAddress] as const;

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Single funnel for the `matchaAccountStateChanged{state, reason}` Amplitude
 * event. Intentionally a no-op for now.
 *
 * TODO(MAT-6637): wire `matchaAccountStateChanged` once the event is approved
 * and synced via ampli. Do NOT call `ampli.*` until then.
 */
const emitStateChange = (_status: AuthStatus, _reason?: string): void => {};

export function AuthProvider({
  children,
  cachedWalletAddresses,
}: {
  children: ReactNode;
  cachedWalletAddresses?: CachedWalletAddresses | null;
}) {
  const [initialCachedWalletAddresses] = useState(() => cachedWalletAddresses ?? null);
  // Privy/wallet hooks run unconditionally (Rules of Hooks); the flag gates the
  // bootstrap query below, not whether these run.
  const enabled = useFeature(MATCHA_ACCOUNT);
  const { ready, authenticated, user } = usePrivy();
  const { identityToken } = useIdentityToken();
  const { wallets } = useWallets();

  // The embedded EVM wallet is the server's required identity, but we don't gate
  // the request on it — the server is the authority and 401s without one. Keying
  // on it makes the session refetch once an embedded wallet resolves.
  const embeddedAddress = getEmbeddedConnectedWallet(wallets)?.address ?? null;

  const query = useQuery({
    queryKey: matchaAccountQueryKey(user?.id ?? null, embeddedAddress),
    enabled: enabled && ready && authenticated && !!identityToken,
    retry: false,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const token = identityToken;
      if (!token) throw new Error('unauthenticated');

      // The query's signal aborts on unmount/key-change; the local timer adds the
      // ~10s bound. A timeout is reported as `timeout`, a TanStack cancellation is
      // rethrown so it's discarded (not surfaced as an error).
      const controller = new AbortController();
      let timedOut = false;
      const onAbort = () => controller.abort();
      signal.addEventListener('abort', onAbort);
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, BOOTSTRAP_TIMEOUT_MS);

      let res: Response;
      try {
        res = await fetch('/api/accounts/bootstrap', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
      } catch (err) {
        if (timedOut) throw new Error('timeout');
        if (signal.aborted) throw err;
        logger.warn('Matcha Account bootstrap failed', {
          error: err instanceof Error ? err.message : String(err),
        });
        throw new Error('network');
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
      }

      if (!res.ok) throw new Error(`http_${res.status}`);
      const parsed = zAccountsPayload.safeParse(await res.json());
      if (!parsed.success) throw new Error('invalid_payload');
      return parsed.data;
    },
  });

  const authState = useMemo<Omit<AuthContextValue, 'cachedWalletAddresses'>>(() => {
    if (!enabled) return { status: 'disabled' };
    if (!ready) return { status: 'loading' };
    // Untrusted: no Privy session or identity token to bootstrap with.
    if (!authenticated || !identityToken) return { status: 'error', error: 'unauthenticated' };
    if (query.isSuccess) {
      return { status: 'ready', account: query.data.account, wallets: query.data.wallets };
    }
    if (query.isError) {
      return {
        status: 'error',
        error: query.error instanceof Error ? query.error.message : 'unknown',
      };
    }
    return { status: 'loading' };
  }, [
    enabled,
    ready,
    authenticated,
    identityToken,
    query.isSuccess,
    query.isError,
    query.data,
    query.error,
  ]);
  const value = useMemo<AuthContextValue>(
    () => ({ ...authState, cachedWalletAddresses: initialCachedWalletAddresses }),
    [authState, initialCachedWalletAddresses],
  );

  // Single emit funnel (stubbed), once per status/reason change.
  useEffect(() => {
    emitStateChange(value.status, value.error);
  }, [value.status, value.error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
